import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../../../context/MyContext";
import { useUserQuery } from "../../../tutorV2/hooks/queries/useUserQuery";
import { usePressCardQuery } from "../queries/usePressCardQuery";
import { useUIStore } from "../../../tutorV2/store";
import { useAuth } from "../../../tutorV2/hooks/useAuth";
import getUserState from "../../../tutorV2/utils/getUserState";

/**
 * myAirV2 페이지 Presentation Hook
 *
 * - tutorV2의 useUserQuery, useAuth 재사용
 * - 모든 메뉴/버튼 클릭 핸들러 제공
 * - User 컴포넌트에 필요한 props 계산
 *
 * @returns user, userState, 각종 핸들러들
 */
export const useMyAirView = () => {
  const navigate = useNavigate();
  const { isMobile, isAppMode, isUnderVersion } = useContext(MyContext);
  const { user, isLoggedIn } = useUserQuery();
  // 로그인 상태일 때 기자증 발급 상태를 조회한다. (userId = profile_id)
  const { isPressCardValid } = usePressCardQuery(user?.id ?? null, isLoggedIn);
  const { showLogoutConfirm, logoutConfirmOpen, setLogoutConfirmOpen, logout } =
    useAuth();
  const { setOpenUserPopover, setShareListModalOpen, closeNav } = useUIStore();

  // User 컴포넌트용 상태 계산 (active/inactive/expired)
  const userState = getUserState(user);

  // 모바일에서 내부 페이지 이동 시 사이드바 닫기
  const closeNavIfMobile = () => {
    if (isMobile) {
      closeNav();
    }
  };

  // 3개 주요 버튼 핸들러
  // /my-air → /my-activity
  const handleActivityClick = () => {
    closeNavIfMobile();
    navigate("/my-activity");
    setOpenUserPopover(false);
  };

  // /my-air → /settings/persona
  const handlePersonaClick = () => {
    navigate("/settings/persona");
    setOpenUserPopover(false);
  };

  // /my-air → /settings/order-list
  const handleOrderClick = () => {
    navigate(
      isAppMode && !isUnderVersion ? "/app-buy-list" : "/settings/order-list",
    );
    setOpenUserPopover(false);
  };

  // 메뉴 리스트 핸들러
  // /my-air → /guide/main
  const handleGuideClick = () => {
    navigate("/guide/main");
    setOpenUserPopover(false);
  };

  // /my-air → 공유목록 모달 열기
  const handleShareClick = () => {
    setShareListModalOpen(true);
    setOpenUserPopover(false);
  };

  // /my-air → 외부 링크 (새 창) - 구독상품 페이지 디지털 탭
  const handleProductsClick = () => {
    if (isAppMode && !isUnderVersion) {
      navigate("/subscription");
      setOpenUserPopover(false);
      return;
    }
    window.open(
      `${process.env.REACT_APP_DONGA_STORE}/subscription?anchor=2`,
      "_blank",
    );
    setOpenUserPopover(false);
  };

  // /my-air → 외부 링크 (새 창)
  const handleMypageClick = () => {
    const url =
      process.env.REACT_APP_DONGA_MYPAGE || "https://my.dongascience.com";
    window.open(url, "_blank");
    setOpenUserPopover(false);
  };

  // 어린이 기자단 메뉴 클릭 (팝플 인앱 브라우저)
  //   - 발급 완료 + 유효(isPressCardValid): 팝플 기자증 페이지로 이동
  //   - 미발급 / 만료                      : 팝플 메인으로 이동
  const handlePressCardClick = () => {
    closeNavIfMobile();
    const planet =
      process.env.REACT_APP_POPCORN_HOST || "https://www.popcornplanet.co.kr";
    const url = isPressCardValid ? `${planet}/mypage/press-card` : planet;
    window.open(url, "_blank");
    setOpenUserPopover(false);
  };

  // /my-air → 외부 링크 (새 창)
  const handleCSClick = () => {
    const url = process.env.REACT_APP_DONGA_INQUERY;
    if (url) {
      window.open(url, "_blank");
    } else {
      console.warn("REACT_APP_DONGA_INQUERY is not defined");
    }
    setOpenUserPopover(false);
  };

  // /my-air → /company-info
  const handleCompanyClick = () => {
    closeNavIfMobile();
    navigate("/company-info");
    setOpenUserPopover(false);
  };

  // User 컴포넌트 구매하기 버튼 핸들러
  // /my-air → /settings/products
  const handlePurchaseClick = () => {
    navigate("/settings/products");
    setOpenUserPopover(false);
  };

  // /my-air → /subscription
  const handleSubscriptionClick = () => {
    closeNavIfMobile();
    if (isAppMode && !isUnderVersion) {
      navigate("/subscription");
      setOpenUserPopover(false);
      return;
    }
    window.open(
      `${process.env.REACT_APP_DONGA_STORE}/subscription?anchor=2`,
      "_blank",
    );
    setOpenUserPopover(false);
  };

  // /my-air → /subscription/test
  const handleTestPageClick = () => {
    closeNavIfMobile();
    navigate("/subscription/test");
    setOpenUserPopover(false);
  };

  // 로그아웃 핸들러
  const handleLogout = () => {
    showLogoutConfirm();
    setOpenUserPopover(false);
  };

  return {
    // 상태
    user,
    isLoggedIn,
    userState,
    isPressCardValid,
    logoutConfirmOpen,
    setLogoutConfirmOpen,
    logout,

    // 핸들러
    handleActivityClick,
    handlePersonaClick,
    handleOrderClick,
    handleGuideClick,
    handleShareClick,
    handleProductsClick,
    handleMypageClick,
    handlePressCardClick,
    handleCSClick,
    handleCompanyClick,
    handlePurchaseClick,
    handleSubscriptionClick,
    handleTestPageClick,
    handleLogout,
  };
};
